import { NavLink } from 'react-router-dom'
import { FilterMatchMode } from 'primereact/api'
import { Alert, ButtonGroup } from 'react-bootstrap'
import { useState } from 'react'
import DeleteDialog from '../../../../common/dialogs/DeleteDialog'
import PageHeader from '../../../../layout/page/PageHeader'
import DataTable from '../../../../common/data-table/DataTable'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit } from '@fortawesome/free-solid-svg-icons'
import { useAppSelector } from '../../../../../store/hooks'
import TrueFalseCell from '../../../../common/data-table/TrueFalseCell'

type TableRow = Record<string, unknown> & { id?: number; name?: string }
type StatusLabels = { trueLabel: string; falseLabel: string }

const ClientIdentities = () => {
    const theme = useAppSelector((state) => state.theme.theme)
    const [refreshTable, setRefreshTable] = useState(false)

    const actionsBodyTemplate = (rowData: TableRow) => {
        return (
            <ButtonGroup size="sm">
                <DeleteDialog
                    url={'/administration/client/identity/delete/' + rowData.id}
                    record={rowData}
                    deletedCallback={() => setRefreshTable(true)}
                    confirmText={rowData.name}
                    message={
                        <>
                            <p className="mb-3 pb-3 border-bottom">
                                Are you sure you want to delete identity{' '}
                                <strong> {rowData.name}</strong>?
                            </p>
                            <Alert variant="warning" className="border-0">
                                <strong>Warning!</strong> All themes related to
                                this identity will be deleted permenantly!
                            </Alert>
                        </>
                    }
                />
                <NavLink
                    to={'/administration/client/identity/view/' + rowData.id}
                    className="btn btn-outline-primary ms-1"
                >
                    <FontAwesomeIcon icon={faEdit} />
                </NavLink>
            </ButtonGroup>
        )
    }

    const statusTemplate = (rowData: TableRow, field: string, labels: StatusLabels) => {
        return (
            <TrueFalseCell
                value={rowData[field] === 1}
                trueLabel={labels.trueLabel}
                falseLabel={labels.falseLabel}
            />
        )
    }

    return (
        <>
            <PageHeader
                icon="address-card"
                iconPrefix="far"
                subtitle="Configure logos, colours, and brand assets"
                gradient="linear-gradient(135deg, #14b8a6, #0d9488)"
                header="Brand Identities"
                buttons={[
                    {
                        variant: theme === 'dark' ? 'dark' : 'light',
                        label: 'Back to Administration',
                        icon: 'fa-solid fa-arrow-left',
                        url: '/administration',
                    },
                    {
                        variant: 'success',
                        label: 'Create New',
                        icon: 'fa-solid fa-plus',
                        url: '/administration/client/identity/new',
                    },
                ]}
            />
            <DataTable
                refreshTable={refreshTable}
                onRefreshed={() => setRefreshTable(false)}
                config={{
                    dataRoute: '/administration/client/identity',
                    first: 0,
                    rows: 10,
                    page: 0,
                    sortField: 'name',
                    sortOrder: 1,
                    filters: {
                        global: {
                            value: null,
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        name: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        business_name: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                        business_slogan: {
                            value: '',
                            matchMode: FilterMatchMode.CONTAINS,
                        },
                    },
                    filterDisplay: 'menu',
                }}
                columns={[
                    {
                        field: 'name',
                        header: 'Brand identity',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        style: {
                            maxWidth: '5rem',
                        },
                    },
                    {
                        field: 'business_name',
                        header: 'Business Name',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        style: {
                            maxWidth: '5rem',
                        },
                    },
                    {
                        field: 'business_slogan',
                        header: 'Slogan',
                        sortable: true,
                        filter: true,
                        filterPlaceholder: 'Search',
                        style: {
                            maxWidth: '3rem',
                        },
                    },
                    {
                        field: 'active',
                        header: 'Active',
                        sortable: true,
                        filter: false,
                        body: (rowData: TableRow) =>
                            statusTemplate(rowData, 'active', {
                                trueLabel: 'Active',
                                falseLabel: 'Inactive',
                            }),
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                        style: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                    },
                    {
                        field: 'default',
                        header: 'Default',
                        sortable: true,
                        filter: false,
                        body: (rowData: TableRow) =>
                            statusTemplate(rowData, 'default', {
                                trueLabel: 'Default',
                                falseLabel: 'Not default',
                            }),
                        fixedCenter: true,
                        alignHeader: 'center',
                        headerStyle: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                        style: {
                            width: '7.5rem',
                            minWidth: '7.5rem',
                            maxWidth: '7.5rem',
                        },
                    },
                    {
                        field: 'id',
                        header: 'Actions',
                        sortable: false,
                        body: actionsBodyTemplate,
                        alignHeader: 'center',
                        headerStyle: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        style: {
                            maxWidth: '1.2rem',
                            textAlign: 'center',
                        },
                        toggleVisibility: false,
                    },
                ]}
                globalFilterFields={[
                    'name',
                    'business_name',
                    'business_slogan',
                ]}
            />
        </>
    )
}

export default ClientIdentities
